import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeService } from '@freelancer/theme';
import { IconColor } from '@freelancer/ui/icon';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import { TabsSize } from '@freelancer/ui/tabs';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-nav',
  template: `
    <div
      class="Header"
      data-testing="sticky-header"
    >
      <div class="Header-inner">
        <div class="Header-primary">
          <div class="LogoContainer mr-xsmall tablet:mr-none">
            <fl-button
              class="MenuButton mr-xsmall desktop:hide"
              (click)="handleMenuButtonClick()"
            >
              <fl-icon
                [name]="'ui-bars-v2'"
                [color]="IconColor.FOREGROUND"
                [useIconFont]="true"
              ></fl-icon>
            </fl-button>
            <fl-logo
              class="Logo mr-xxxsmall"
              [backgroundColor]="BackgroundColor.THEME"
              [size]="LogoSize.SMALL"
              (click)="onClick()"
            ></fl-logo>
          </div>

          <div class="RightSide">
            <app-theme-toggle class="desktop:hide" />
            <app-search
              class="Header-search desktop:hide"
              [isExpandable]="true"
              [hideInternalPages]="hideInternalPages"
              [expandableIconLight]="false"
              [openInModal]="true"
            ></app-search>
          </div>
        </div>
        @if ((storyOnly$ | flAsync) === false) {
          @if (isHomepage) {
            <div class="Spacer"></div>
          }
          <app-nav-tabs
            [size]="TabsSize.LARGE"
            [sizeDesktop]="TabsSize.XLARGE"
            [hideInternalPages]="hideInternalPages"
            [class.mobile:hide]="isHomepage"
            [class.tablet:hide]="isHomepage"
          ></app-nav-tabs>
          <div class="RightSide">
            <app-theme-toggle class="ThemeToggle mobile:hide tablet:hide" />
            <app-search
              class="Header-search Header-searchDesktop desktop:show"
              [hideInternalPages]="hideInternalPages"
              [enableKeyboardShortcut]="true"
              [showShortcutKey]="true"
              [openInModal]="true"
            ></app-search>
          </div>
        }
      </div>
    </div>
  `,
  styleUrls: ['./nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private themeService = inject(ThemeService);

  IconColor = IconColor;
  LogoSize = LogoSize;
  BackgroundColor = BackgroundColor;
  TabsSize = TabsSize;

  storyOnly$: Observable<boolean>;

  @Output() menuButtonClicked = new EventEmitter<void>();
  @Input() hideInternalPages = false;
  @Input() isHomepage = false;
  currentTheme$: Observable<string>;

  ngOnInit(): void {
    this.currentTheme$ = this.themeService.getTheme();
    this.storyOnly$ = this.activatedRoute.queryParamMap.pipe(
      map(params => isDefined(params.get('story_only'))),
    );
  }

  onClick(): void {
    this.router.navigate(['/']);
  }

  handleMenuButtonClick(): void {
    this.menuButtonClicked.emit();
  }
}
